import { Component, OnInit } from '@angular/core';
import { CommonModule } from '@angular/common';
import {
  FormBuilder,
  FormGroup,
  FormsModule,
  ReactiveFormsModule,
  Validators,
} from '@angular/forms';
import { MatDialogModule, MatDialogRef } from '@angular/material/dialog';
import { MaterialModule } from 'src/app/material.module';
import { AuthService } from 'src/app/services/authService';

@Component({
  selector: 'app-reauth-dialog',
  standalone: true,
  imports: [
    CommonModule,
    MaterialModule,
    MatDialogModule,
    FormsModule,
    ReactiveFormsModule,
  ],
  template: `
    <h2 mat-dialog-title>Sesión caducada</h2>
    <mat-dialog-content>
      <p class="reauth-intro">
        Por inactividad su sesión caducó. Ingrese su contraseña para continuar
        en la misma pantalla, o cierre sesión.
      </p>
      <p class="reauth-user" *ngIf="nombre || correo">
        <strong>{{ nombre || 'Usuario' }}</strong>
        <span *ngIf="correo" class="reauth-correo">{{ correo }}</span>
      </p>
      <form [formGroup]="form" (ngSubmit)="continuar()">
        <mat-form-field appearance="outline" class="w-100">
          <mat-label>Contraseña</mat-label>
          <input
            matInput
            type="password"
            formControlName="password"
            autocomplete="current-password"
          />
          <mat-error *ngIf="form.get('password')?.hasError('required')">
            La contraseña es requerida
          </mat-error>
        </mat-form-field>
        <p class="reauth-error" *ngIf="errorMessage">{{ errorMessage }}</p>
      </form>
    </mat-dialog-content>
    <mat-dialog-actions align="end">
      <button
        mat-raised-button
        type="button"
        class="reauth-logout-btn"
        [disabled]="isLoading"
        (click)="cerrarSesion()"
      >
        Cerrar sesión
      </button>
      <button
        mat-raised-button
        color="primary"
        type="button"
        [disabled]="isLoading || form.invalid"
        (click)="continuar()"
      >
        {{ isLoading ? 'Validando…' : 'Continuar' }}
      </button>
    </mat-dialog-actions>
  `,
  styles: [
    `
      .reauth-intro {
        margin: 0 0 12px;
        line-height: 1.45;
      }
      .reauth-user {
        margin: 0 0 16px;
        display: flex;
        flex-direction: column;
        gap: 2px;
      }
      .reauth-correo {
        font-size: 13px;
        opacity: 0.8;
      }
      .reauth-error {
        color: #c62828;
        font-size: 13px;
        margin: 0 0 8px;
      }
      .w-100 {
        width: 100%;
      }
      mat-dialog-content {
        min-width: 280px;
        max-width: 420px;
      }
      .reauth-logout-btn {
        background-color: #e53935 !important;
        color: #fff !important;
      }
      .reauth-logout-btn:hover:not([disabled]) {
        background-color: #c62828 !important;
      }
      .reauth-logout-btn[disabled] {
        opacity: 0.55;
      }
    `,
  ],
})
export class ReauthDialogComponent implements OnInit {
  form: FormGroup;
  nombre = '';
  correo = '';
  errorMessage = '';
  isLoading = false;

  constructor(
    private fb: FormBuilder,
    private authService: AuthService,
    private dialogRef: MatDialogRef<ReauthDialogComponent, 'reauthed' | 'logout'>,
  ) {
    this.form = this.fb.group({
      password: ['', [Validators.required, Validators.minLength(8)]],
    });
  }

  ngOnInit(): void {
    this.nombre = localStorage.getItem('nombre') || '';
    this.correo = localStorage.getItem('correo') || '';
    this.dialogRef.disableClose = true;
  }

  continuar(): void {
    if (this.form.invalid || this.isLoading) {
      return;
    }
    this.errorMessage = '';
    this.isLoading = true;
    const password = this.form.value.password as string;
    this.authService.reauthenticate(password).subscribe((result) => {
      this.isLoading = false;
      if (result.success) {
        this.dialogRef.close('reauthed');
        return;
      }
      this.errorMessage = result.error || 'Contraseña incorrecta.';
      this.form.patchValue({ password: '' });
    });
  }

  cerrarSesion(): void {
    if (this.isLoading) {
      return;
    }
    this.dialogRef.close('logout');
    void this.authService.logout();
  }
}
